import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';

const PLACEHOLDER = 'MM / DD / YYYY';
const SELECT_DATE_TYPE = { INC: 'INC', DEC: 'DEC' };

export default class DatePicker extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      expanded: false,
      inputDate: props.selectedDate ? moment(props.selectedDate).format(PLACEHOLDER) : '',
    };

    this.cursorPosition = 0;
    this.temporaryDate = props.selectedDate ? moment(props.selectedDate) : moment.utc();
    this.months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ];
    this.inputFieldRef = {};
  }

  shouldComponentUpdate({ selectedDate }) {
    const { inputDate } = this.state;

    if (selectedDate.isValid && selectedDate.isValid() && !selectedDate.isSame(this.props.selectedDate)) {
      this.setState({ inputDate: selectedDate.format(PLACEHOLDER) });
    } else if (!selectedDate && moment(inputDate, PLACEHOLDER).isValid()) {
      this.setState({ inputDate: '' });
    }

    return true;
  }

  _toggle = () => {
    const { expanded } = this.state;

    if (!expanded) {
      document.addEventListener('click', this._toggle);
      this.setState({ expanded: true });
    } else {
      document.removeEventListener('click', this._toggle);
      this.setState({ expanded: false });
    }
  };

  __getIEKey = keyCode => {
    if (keyCode > 95 && keyCode < 106) return keyCode - 96; // Numpad

    if (keyCode > 47 && keyCode < 58) return keyCode - 48; // Numbers

    if (keyCode === 37 || keyCode === 39) {
      // Arrows
      switch (keyCode) {
        case 37:
          return 'ArrowLeft';
        case 39:
          return 'ArrowRight';
        default:
          return null;
      }
    }

    return null;
  };

  _handleDateInput = event => {
    const { handleSelectDate } = this.props;

    this.cursorPosition = event.target.selectionStart;

    const target = event.target;
    let key = event.nativeEvent.key;

    if (!key) {
      key = this.__getIEKey(event.which);
    }
    switch (key) {
      case 'ArrowLeft':
        return this.__updateLeftCursorPosition(event);
      case 'ArrowRight':
        return this.__updateRightCursorPosition(event);
      case 'Delete':
        return this.__deleteRightCursorPosition(event);
      case 'Backspace':
        return this.__deleteLeftCursorPosition(event);
      default:
        break;
    }

    if (this.cursorPosition !== 14 && isFinite(key) && key !== null) {
      const inputDate = this.__updateInputDate(key);

      this.setState({ inputDate }, () => {
        const date = moment.utc(`${inputDate.slice(0, 14)}Z`, PLACEHOLDER);

        if (date.isValid()) {
          this.temporaryDate = date;
          handleSelectDate(date);
          this.forceUpdate();
        } else {
          handleSelectDate('', true);
        }

        target.setSelectionRange(this.cursorPosition + 1, this.cursorPosition + 1);
      });
    }
  };

  __updateLeftCursorPosition = event => {
    if (this.cursorPosition > 2 && this.cursorPosition < 6) {
      this.cursorPosition = 3;
      event.target.setSelectionRange(this.cursorPosition, this.cursorPosition);
    }

    if (this.cursorPosition > 7 && this.cursorPosition < 11) {
      this.cursorPosition = 8;
      event.target.setSelectionRange(this.cursorPosition, this.cursorPosition);
    }
  };

  __updateRightCursorPosition = event => {
    if (this.cursorPosition > 1 && this.cursorPosition < 5) {
      this.cursorPosition = 4;
      event.target.setSelectionRange(this.cursorPosition, this.cursorPosition);
    }

    if (this.cursorPosition > 6 && this.cursorPosition < 10) {
      this.cursorPosition = 9;
      event.target.setSelectionRange(this.cursorPosition, this.cursorPosition);
    }
  };

  __deleteLeftCursorPosition = () => {};

  __deleteRightCursorPosition = () => {};

  __updateInputDate = key => {
    if (this.cursorPosition === 1) {
      this.cursorPosition = 4;
      return this.state.inputDate.slice(0, 1) + key + ' / ' + this.state.inputDate.slice(5);
    }

    if (this.cursorPosition === 6) {
      this.cursorPosition = 9;
      return this.state.inputDate.slice(0, 6) + key + ' / ' + this.state.inputDate.slice(10);
    }

    if (this.cursorPosition > 1 && this.cursorPosition < 5) {
      this.cursorPosition = 5;
      return this.state.inputDate.slice(0, 2) + ' / ' + key + this.state.inputDate.slice(6);
    }

    if (this.cursorPosition > 6 && this.cursorPosition < 10) {
      this.cursorPosition = 10;
      return this.state.inputDate.slice(0, 7) + ' / ' + key + this.state.inputDate.slice(11);
    }

    return (
      this.state.inputDate.slice(0, this.cursorPosition) + key + this.state.inputDate.slice(this.cursorPosition + 1)
    );
  };

  _clearDate = event => {
    const { handleSelectDate } = this.props;

    event.stopPropagation();
    if (event.nativeEvent) event.nativeEvent.stopImmediatePropagation();

    this.setState({ inputDate: '' }, () => {
      handleSelectDate('');
    });
  };

  _handleYearSelect = selectType => event => {
    event.stopPropagation();
    if (event.nativeEvent) event.nativeEvent.stopImmediatePropagation();

    switch (selectType) {
      case SELECT_DATE_TYPE.INC:
        this.temporaryDate.year(this.temporaryDate.year() + 1);
        break;
      case SELECT_DATE_TYPE.DEC:
        this.temporaryDate.year(this.temporaryDate.year() - 1);
        break;
      default:
        break;
    }

    this.forceUpdate();
  };

  _handleMonthSelect = selectType => event => {
    event.stopPropagation();
    if (event.nativeEvent) event.nativeEvent.stopImmediatePropagation();

    switch (selectType) {
      case SELECT_DATE_TYPE.INC:
        this.temporaryDate.month(this.temporaryDate.month() + 1);
        break;
      case SELECT_DATE_TYPE.DEC:
        this.temporaryDate.month(this.temporaryDate.month() - 1);
        break;
      default:
        break;
    }

    this.forceUpdate();
  };

  _handleDaySelect = day => event => {
    const { handleSelectDate } = this.props;

    event.stopPropagation();
    if (event.nativeEvent) event.nativeEvent.stopImmediatePropagation();

    this.temporaryDate.date(day);

    this.setState({ inputDate: this.temporaryDate.format(PLACEHOLDER) }, () => {
      handleSelectDate(this.temporaryDate);
      this._toggle();
    });
  };

  _getDays = () => {
    const days = [];

    for (let i = 0, l = this.temporaryDate.daysInMonth(); i < l; i++) {
      days.push(
        <div
          key={i}
          className={`day ${i + 1 === this.temporaryDate.date() ? 'active' : ''}`}
          onClick={this._handleDaySelect(i + 1)}
        >
          {i + 1}
        </div>
      );
    }

    return days;
  };

  saveInputFieldRef = ref => {
    this.inputFieldRef = ref;
  };

  render() {
    const { inputDate, expanded } = this.state;
    const { error, label, mainWrapperClassName, noInput } = this.props;
    const date = moment(this.temporaryDate, PLACEHOLDER);

    const dropDownLeftPosition = noInput ? '-72px' : '0px';
    const dropDownTopPosition = noInput ? '25px' : label ? '59px' : '54px';

    return (
      <div className={`date-picker-wrapper ${noInput ? 'date-picker-wrapper_noInput' : ''} ${mainWrapperClassName}`}>
        {label && <label>{label}</label>}
        {!noInput && (
          <div className='date-picker-input-wrapper'>
            <input
              className={`date-input ${error ? 'error' : ''}`}
              value={inputDate}
              placeholder={PLACEHOLDER}
              onKeyDown={this._handleDateInput}
              onFocus={this._handleDateInput}
              spellCheck={false}
              ref={this.saveInputFieldRef}
              autoComplete='off'
            />
            {inputDate && <div className='date-picker-clear-button' onClick={this._clearDate} />}
            {typeof error === 'string' && <div className='error-text'>{error}</div>}
          </div>
        )}
        <div className='date-picker-open-button' onClick={this._toggle} />
        {expanded ? (
          <div className='date-picker-dropdown' style={{ top: dropDownTopPosition, left: dropDownLeftPosition }}>
            <div className='year-choose-section'>
              <div className='arrow decrease' onClick={this._handleYearSelect(SELECT_DATE_TYPE.DEC)}>
                <div />
              </div>
              <div className='text-holder'>{date.year()}</div>
              <div className='arrow increase' onClick={this._handleYearSelect(SELECT_DATE_TYPE.INC)}>
                <div />
              </div>
            </div>
            <div className='month-choose-section'>
              <div className='arrow decrease' onClick={this._handleMonthSelect(SELECT_DATE_TYPE.DEC)}>
                <div />
              </div>
              <div className='text-holder'>{this.months[date.month()]}</div>
              <div className='arrow increase' onClick={this._handleMonthSelect(SELECT_DATE_TYPE.INC)}>
                <div />
              </div>
            </div>
            <div className='day-choose-section'>{this._getDays()}</div>
          </div>
        ) : null}
      </div>
    );
  }
}

DatePicker.propTypes = {
  selectedDate: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  handleSelectDate: PropTypes.func,
  label: PropTypes.string,
  error: PropTypes.string,
  mainWrapperClassName: PropTypes.string,
  noInput: PropTypes.bool,
};

DatePicker.defaultProps = {
  selectedDate: '',
  handleSelectDate: () => {},
  label: '',
  error: '',
  mainWrapperClassName: '',
  noInput: false,
};



// WEBPACK FOOTER //
// ./src/app/Components/DatePicker.jsx